// Interaction solver (SA B5, B5.1) — pure, deterministic, DOM-free.
//
// Input every frame: each person's keypoints in UN-MIRRORED stage pixels
// (parts.ts → solverPerson), the host's LayoutState and the time.
// Output: the ordered list of body-part layers for the whole stage, contacts
// (for haptics / contact shadows), per-person readiness + Vietnamese hints,
// depth hints ("Bước lên một chút…") and ghost targets for coaching.
//
// Rules (SA B5):
//  1. Base order = virtual depth (B5.1): z = −SLOT_DEPTH·front − DEPTH_K·ln(IPD_now / IPD_ref).
//     Stepping toward your camera grows the IPD → z shrinks → you come forward.
//     A swap needs |Δz| > DEPTH_DEADBAND held for ≥ DEPTH_HOLD_MS.
//  2. Arm over shoulder A → B: hand of A in B's shoulder/upper-chest region, A's
//     elbow between the two shoulder midpoints, A's wrist roughly level in z with
//     A's shoulder. Then arms_front(A) goes above body(B) and head(B); body(A)
//     keeps its base place.
//  3. Arm behind B's back: elbow between, wrist behind (z) or hidden, hand inside
//     B's torso/shoulder region → arms_back(A) goes below body(B).
//  4. Hysteresis: every decision changes only after the new raw result held for
//     ARM_HOLD_MS, with ~10 % deadband around each threshold (enter vs exit).
//  5. Deterministic: inputs are on a 1/1024 grid (parts.ts), no order-dependent
//     maths, ties broken by peer id. Mirroring the whole input mirrors the output
//     (same decisions per anatomical arm).

import type { EyeAnchor } from './layout';
import type { SolverPerson } from './parts';
import { ARM_JOINTS, KP, type Side } from './pose';

// ------------------------------------------------------------------ public types (contract with the UI)

export type Template = 'side' | 'heads' | 'shoulder';

export interface LayoutSlot {
  peerId: string;
  /** slot centre in DISPLAY space (what everybody sees in the shared mirror), 0..1 */
  x: number;
  /** "who stands in front" override; higher = nearer. Body movement (B5.1) still applies on top. */
  front: number;
  /** optional display name used in hints */
  name?: string;
}

/** Host-authoritative layout (SA B2). Applied in increasing `version` order. */
export interface LayoutState {
  version: number;
  template: Template;
  /** display the stage mirrored (shared-mirror convention; default true) */
  mirror: boolean;
  slots: LayoutSlot[];
  /** saved photo: people area mirrored like the live mirror (true, default) or camera view (false) */
  mirrorPhoto: boolean;
  /** optional: who should put the arm over in 'shoulder' (otherwise picked automatically) */
  giver?: string;
  /**
   * v0.7 'natural' (default): real camera scale, the nearer person (bigger face) stands in
   * front; 'matched': v0.5 faces normalised to one size, order from the slots + stepping forward
   */
  scale?: 'natural' | 'matched';
}

export type ContactKind = 'hand-shoulder' | 'head-head';
export interface Contact { a: string; b: string; kind: ContactKind }

/** Emitted by stage.onInteraction (≤ 10 Hz, on change). */
export interface InteractionState {
  contacts: Contact[];
  ready: Record<string, boolean>;
  hints: Record<string, string>;
  depthHints: Record<string, string>;
}

export type LayerKind = 'arms_back' | 'body' | 'head' | 'arms_front';
export interface LayerRef { id: string; kind: LayerKind }
export type ArmState = 'none' | 'front' | 'back';
export interface ArmDecision { state: ArmState; partner: string | null }

/** Cross-fade weight while two overlapping people swap depth (soft-z only inside overlaps). */
export interface SoftZ { over: string; under: string; w: number }

/** Everything the compositor needs to draw one frame; captured at the shutter for the still (US7). */
export interface Decision {
  v: 1;
  template: Template;
  /** back → front */
  order: LayerRef[];
  arms: Record<string, Record<Side, ArmDecision>>;
  contacts: Contact[];
  /** virtual depth per person (smaller = nearer) */
  depth: Record<string, number>;
  softZ: SoftZ | null;
  /** filled by stage.snapshot(): the layout slots / mirror flags at the shutter, so the still places people identically */
  slots?: LayoutSlot[];
  mirror?: boolean;
  mirrorPhoto?: boolean;
  /** filtered eye anchors used for placement at the shutter (the still uses each person's own device's value) */
  anchors?: Record<string, EyeAnchor>;
  /** v0.7: placed at real scale (layout.scale 'natural') */
  natural?: boolean;
  /** v0.7.1: virtual-camera framing of the group at the shutter */
  view?: { z: number; tx: number; ty: number };
}

/** Coaching target in UN-MIRRORED stage px. */
export interface Ghost { owner: string; kind: 'hand' | 'head'; x: number; y: number; r: number; matched: boolean }
export interface ContactPoint extends Contact { x: number; y: number }

export interface SolverOutput {
  decision: Decision;
  interaction: InteractionState;
  ghosts: Ghost[];
  contactPoints: ContactPoint[];
}

// ------------------------------------------------------------------ tuning

export const ARM_HOLD_MS = 200;
export const DEPTH_HOLD_MS = 250;
export const DEPTH_DEADBAND = 0.06;
export const DEPTH_K = 1;
export const SLOT_DEPTH = 0.15;
/** head radius (fraction of the stage height) of somebody at a normal selfie distance */
export const NOMINAL_HEAD = 0.12;
export const HEADS_RANGE = { enter: [0.05, 0.25], exit: [0.03, 0.3] } as const;
const VIS = 0.35;
/** IPD reference: captured once the IPD stayed within ±3 % for this long */
const IPD_STABLE_MS = 600;
const IPD_LOST_MS = 2000;

export const LAYER_KINDS: LayerKind[] = ['arms_back', 'body', 'head', 'arms_front'];

export const HINT = {
  noBody: 'Lùi lại một chút cho thấy cả hai vai',
  noFace: 'Nhìn thẳng vào camera',
  eyeLine: (b: string) => `Chỉnh cho mắt ngang hàng với ${b}`,
  leanTo: (b: string) => `Nghiêng đầu về phía ${b}`,
  apart: 'Tách ra một chút',
  higher: 'Đưa tay cao hơn một chút',
  reach: (b: string) => `Vòng tay sang vai ${b}`,
  elbow: (b: string) => `Hạ khuỷu tay xuống vai ${b}`,
  forward: (b: string) => `Đưa tay ra phía trước vai ${b}`,
  showHand: 'Giơ tay lên cho thấy bàn tay',
  hold: (a: string) => `Đứng yên, ${a} sẽ quàng vai bạn`,
  stepUp: (b: string) => `Bước lên một chút để đứng trước ${b}`,
  steady: 'Giữ yên…',
};

export function defaultLayout(ids: string[], template: Template = 'side'): LayoutState {
  const n = ids.length;
  return {
    version: 0, template, mirror: true, mirrorPhoto: true, scale: 'natural',
    slots: ids.map((peerId, i) => ({ peerId, x: n <= 1 ? 0.5 : 0.5 - Math.min(0.8, 0.4 * (n - 1)) / 2 + (Math.min(0.8, 0.4 * (n - 1)) * i) / (n - 1), front: 0 })),
  };
}

// ------------------------------------------------------------------ helpers

class Latch<T> {
  cand: T;
  since: number;
  constructor(public v: T, t: number) { this.cand = v; this.since = t; }
  step(raw: T, t: number, hold: number): T {
    if (raw === this.v) { this.cand = raw; return this.v; }
    if (raw !== this.cand) { this.cand = raw; this.since = t; }
    if (t - this.since >= hold) this.v = raw;
    return this.v;
  }
  get pending() { return this.cand !== this.v; }
}

type P = SolverPerson;
const X = (p: P, i: number) => p.kp![i * 4];
const Y = (p: P, i: number) => p.kp![i * 4 + 1];
const Z = (p: P, i: number) => p.kp![i * 4 + 2];
const V = (p: P, i: number) => p.kp![i * 4 + 3];
const hasBody = (p: P) => !!p.kp && p.sw != null && V(p, KP.leftShoulder) >= VIS && V(p, KP.rightShoulder) >= VIS;
const shoulderMid = (p: P): [number, number] =>
  [(X(p, KP.leftShoulder) + X(p, KP.rightShoulder)) / 2, (Y(p, KP.leftShoulder) + Y(p, KP.rightShoulder)) / 2];
const byId = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
const smooth = (e0: number, e1: number, x: number) => { const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); };

/** Hand point in stage px (mean of visible wrist / index / pinky); `hidden` when the wrist itself is not visible. */
function hand(p: P, side: Side): { x: number; y: number; hidden: boolean } {
  const j = ARM_JOINTS[side];
  const pts = [j.wrist, j.index, j.pinky].filter((i) => V(p, i) >= VIS);
  if (!pts.length || V(p, j.wrist) < VIS) return { x: X(p, j.wrist), y: Y(p, j.wrist), hidden: true };
  return { x: pts.reduce((a, i) => a + X(p, i), 0) / pts.length, y: pts.reduce((a, i) => a + Y(p, i), 0) / pts.length, hidden: false };
}

interface Region { x0: number; x1: number; y0: number; y1: number }
const inR = (r: Region, x: number, y: number) => x >= r.x0 && x <= r.x1 && y >= r.y0 && y <= r.y1;

function shoulderRegion(b: P, e: number): Region {
  const sw = b.sw!, xs = [X(b, KP.leftShoulder), X(b, KP.rightShoulder)], y = shoulderMid(b)[1];
  const m = (0.35 + 0.1 * e) * sw;
  return { x0: Math.min(...xs) - m, x1: Math.max(...xs) + m, y0: y - (0.6 + 0.1 * e) * sw, y1: y + (0.55 + 0.1 * e) * sw };
}

function torsoRegion(b: P, e: number): Region {
  const sw = b.sw!, xs = [X(b, KP.leftShoulder), X(b, KP.rightShoulder)], y = shoulderMid(b)[1];
  const m = (0.15 + 0.1 * e) * sw;
  return { x0: Math.min(...xs) - m, x1: Math.max(...xs) + m, y0: y + (0.15 - 0.1 * e) * sw, y1: y + (1.9 + 0.1 * e) * sw };
}

/** Parameter of A's elbow on the line between the two shoulder midpoints (0 = A, 1 = B), or null. */
function elbowT(a: P, side: Side, b: P): number | null {
  const el = ARM_JOINTS[side].elbow;
  if (V(a, el) < VIS) return null;
  const ma = shoulderMid(a)[0], mb = shoulderMid(b)[0];
  const d = mb - ma;
  if (Math.abs(d) < 0.2 * a.sw!) return null;
  return (X(a, el) - ma) / d;
}

/** Raw (un-latched) arm classification of A's `side` arm against partner B. `e` = 1 when currently engaged (exit thresholds). */
function classifyArm(a: P, side: Side, b: P, e: number): ArmState {
  const t = elbowT(a, side, b);
  if (t == null || t < 0.1 - 0.12 * e || t > 1.3 + 0.15 * e) return 'none';
  const h = hand(a, side);
  const dz = Z(a, ARM_JOINTS[side].wrist) - Z(a, ARM_JOINTS[side].shoulder);
  const sh = shoulderRegion(b, e);
  if (!h.hidden && inR(sh, h.x, h.y) && dz < 0.5 + 0.1 * e) return 'front';
  // behind the partner's back: the arm must clearly reach across (a hanging arm whose hand is
  // simply out of frame also reads as "hidden", but its elbow and hand stay at the owner's side)
  const ma = shoulderMid(a)[0], dir = Math.sign(shoulderMid(b)[0] - ma), handT = (h.x - ma) / (shoulderMid(b)[0] - ma);
  const j = ARM_JOINTS[side];
  const reach = (X(a, j.elbow) - X(a, j.shoulder)) * dir; // how far the elbow left the own shoulder toward B
  const behind = h.hidden || dz > 0.5 - 0.1 * e;
  if (behind && t >= 0.25 - 0.1 * e && handT >= 0.5 - 0.1 * e && reach > (0.3 - 0.1 * e) * a.sw! &&
    (inR(torsoRegion(b, e), h.x, h.y) || inR(sh, h.x, h.y))) return 'back';
  return 'none';
}

/** Coaching target for A's hand: on top of B's shoulder nearest to A. */
function handTarget(a: P, b: P): { x: number; y: number; r: number } {
  const ma = shoulderMid(a)[0];
  const ls = [X(b, KP.leftShoulder), Y(b, KP.leftShoulder)], rs = [X(b, KP.rightShoulder), Y(b, KP.rightShoulder)];
  const near = Math.abs(ls[0] - ma) < Math.abs(rs[0] - ma) ? ls : rs;
  const far = near === ls ? rs : ls;
  const sw = b.sw!;
  // a little inward from the shoulder tip, a little above it (the hand rests on top)
  return { x: near[0] + (far[0] - near[0]) * 0.18, y: near[1] - 0.12 * sw, r: 0.3 * sw };
}

// ------------------------------------------------------------------ the solver

export interface SolverOptions {
  armHoldMs?: number;
  depthHoldMs?: number;
}

export class InteractionSolver {
  private arms = new Map<string, Latch<string>>();
  private pairs = new Map<string, Latch<string>>();
  private heads = new Map<string, Latch<boolean>>();
  private giver: Latch<string> | null = null;
  private ipdHist = new Map<string, { t: number; v: number }[]>();
  private ipdRef = new Map<string, number>();
  private lastSeen = new Map<string, number>();
  private armHold: number;
  private depthHold: number;

  constructor(o: SolverOptions = {}) {
    this.armHold = o.armHoldMs ?? ARM_HOLD_MS;
    this.depthHold = o.depthHoldMs ?? DEPTH_HOLD_MS;
  }

  /** Forget the IPD reference (e.g. after the person changed slot); all when no id. */
  resetDepth(id?: string) {
    if (id == null) { this.ipdRef.clear(); this.ipdHist.clear(); return; }
    this.ipdRef.delete(id);
    this.ipdHist.delete(id);
  }

  /** IPD reference of a person (captured when they first stood still in the slot). */
  ipdReference(id: string) { return this.ipdRef.get(id) ?? null; }

  private trackIpd(p: P, t: number) {
    if (p.ipd == null) return;
    this.lastSeen.set(p.id, t);
    if (this.ipdRef.has(p.id)) return;
    const h = this.ipdHist.get(p.id) ?? [];
    h.push({ t, v: p.ipd });
    while (h.length && t - h[0].t > IPD_STABLE_MS + 200) h.shift();
    this.ipdHist.set(p.id, h);
    if (h.length >= 3 && t - h[0].t >= IPD_STABLE_MS) {
      const vs = h.map((s) => s.v);
      const lo = Math.min(...vs), hi = Math.max(...vs);
      if (hi / lo < 1.06) {
        const sorted = [...vs].sort((x, y) => x - y);
        this.ipdRef.set(p.id, sorted[sorted.length >> 1]);
      }
    }
  }

  private depthOf(p: P, natural = false, stageH = 720): number {
    // real scale: whoever LOOKS bigger on the stage (head size in stage px) is nearer
    if (natural) return p.ipd && p.head ? Math.round(-DEPTH_K * Math.log(p.head.r / (NOMINAL_HEAD * stageH)) * 1024) / 1024 : 0;
    const ref = this.ipdRef.get(p.id);
    const body = ref && p.ipd ? -DEPTH_K * Math.log(p.ipd / ref) : 0;
    return Math.round((-SLOT_DEPTH * p.front + body) * 1024) / 1024;
  }

  update(people: SolverPerson[], layout: LayoutState, t: number, stageH = 720): SolverOutput {
    const ppl = [...people].sort((a, b) => byId(a.id, b.id));
    const ids = ppl.map((p) => p.id);
    const get = (id: string) => ppl.find((p) => p.id === id)!;
    const name = (id: string) => layout.slots.find((s) => s.peerId === id)?.name || id;
    for (const p of ppl) this.trackIpd(p, t);
    for (const [id, seen] of this.lastSeen) {
      if (t - seen > IPD_LOST_MS) { this.ipdRef.delete(id); this.ipdHist.delete(id); this.lastSeen.delete(id); }
    }

    // ---- 1. base order from virtual depth (pairwise latches, deadband + hold)
    const depth: Record<string, number> = {};
    const natural = layout.scale !== 'matched';
    for (const p of ppl) depth[p.id] = this.depthOf(p, natural, stageH);
    const pairKey = (a: string, b: string) => `${a}|${b}`; // a < b
    const nearer = (a: string, b: string): string => { // a < b; returns the id that is in front
      const k = pairKey(a, b);
      const za = depth[a], zb = depth[b];
      let l = this.pairs.get(k);
      if (!l) this.pairs.set(k, (l = new Latch(za < zb ? a : b, t)));
      const raw = za < zb - DEPTH_DEADBAND ? a : zb < za - DEPTH_DEADBAND ? b : l.v;
      return l.step(raw, t, this.depthHold);
    };
    const front: Record<string, string> = {};
    for (let i = 0; i < ids.length; i++) for (let j = i + 1; j < ids.length; j++) front[pairKey(ids[i], ids[j])] = nearer(ids[i], ids[j]);
    // back → front: count how many others each person is in front of (stable for ≤ 8 people, ties by id)
    const wins = (id: string) => ids.filter((o) => o !== id && front[pairKey(id < o ? id : o, id < o ? o : id)] === id).length;
    const base = [...ids].sort((a, b) => wins(a) - wins(b) || byId(a, b));

    // ---- 2. arms
    const arms: Record<string, Record<Side, ArmDecision>> = {};
    const contactPoints: ContactPoint[] = [];
    for (const a of ppl) {
      arms[a.id] = { left: { state: 'none', partner: null }, right: { state: 'none', partner: null } };
      if (!hasBody(a)) continue;
      for (const side of ['left', 'right'] as Side[]) {
        const key = `${a.id}|${side}`;
        let l = this.arms.get(key);
        if (!l) this.arms.set(key, (l = new Latch('none', t)));
        const [curState, curPartner] = l.v.split(':');
        // nearest partner to the hand (ties by id)
        const h = hand(a, side);
        const partners = ppl.filter((b) => b !== a && hasBody(b))
          .map((b) => ({ b, d: Math.hypot(shoulderMid(b)[0] - h.x, shoulderMid(b)[1] - h.y) }))
          .sort((u, v) => u.d - v.d || byId(u.b.id, v.b.id));
        let raw = 'none';
        for (const { b } of partners) {
          const e = curState !== 'none' && curPartner === b.id ? 1 : 0;
          const s = classifyArm(a, side, b, e);
          if (s !== 'none') { raw = `${s}:${b.id}`; break; }
        }
        const v = l.step(raw, t, this.armHold);
        const [state, partner] = v.split(':');
        if (state !== 'none' && partner && ids.includes(partner)) {
          arms[a.id][side] = { state: state as ArmState, partner };
          if (state === 'front') contactPoints.push({ a: a.id, b: partner, kind: 'hand-shoulder', x: h.x, y: h.y });
        }
      }
    }
    for (const k of [...this.arms.keys()]) if (!ids.includes(k.split('|')[0])) this.arms.delete(k);

    // ---- 3. layer order
    const order: LayerRef[] = base.flatMap((id) => LAYER_KINDS.map((kind) => ({ id, kind })));
    const idx = (id: string, kind: LayerKind) => order.findIndex((l) => l.id === id && l.kind === kind);
    const move = (from: number, to: number) => { const [x] = order.splice(from, 1); order.splice(to > from ? to - 1 : to, 0, x); };
    for (const id of ids) {
      for (const side of ['left', 'right'] as Side[]) {
        const d = arms[id][side];
        if (d.state === 'front') {
          const i = idx(id, 'arms_front');
          const top = Math.max(idx(d.partner!, 'body'), idx(d.partner!, 'head'));
          if (i < top) move(i, top + 1);
        } else if (d.state === 'back') {
          const i = idx(id, 'arms_back');
          const b = idx(d.partner!, 'body');
          if (i > b) move(i, b);
        }
      }
    }

    // ---- 4. readiness, hints, ghosts per template
    const ready: Record<string, boolean> = {};
    const hints: Record<string, string> = {};
    const depthHints: Record<string, string> = {};
    const ghosts: Ghost[] = [];
    const contacts: Contact[] = contactPoints.map(({ a, b, kind }) => ({ a, b, kind }));
    for (const p of ppl) {
      depthHints[p.id] = '';
      if (!hasBody(p)) { ready[p.id] = false; hints[p.id] = HINT.noBody; }
      else if (p.ipd == null) { ready[p.id] = false; hints[p.id] = HINT.noFace; }
      else { ready[p.id] = true; hints[p.id] = ''; }
    }
    const okIds = ids.filter((id) => ready[id]);
    // stage-x neighbours (left → right in un-mirrored stage space; symmetric under mirroring)
    const byX = [...okIds].sort((a, b) => shoulderMid(get(a))[0] - shoulderMid(get(b))[0] || byId(a, b));
    const neighbours: [string, string][] = [];
    for (let i = 0; i + 1 < byX.length; i++) neighbours.push(byX[i] < byX[i + 1] ? [byX[i], byX[i + 1]] : [byX[i + 1], byX[i]]);

    let softZ: SoftZ | null = null;
    if (layout.template === 'side') {
      for (const [a, b] of neighbours) {
        const ha = get(a).head, hb = get(b).head;
        if (ha && hb && Math.abs(ha.y - hb.y) > 0.03 * stageH) {
          for (const [x, y] of [[a, b], [b, a]]) { ready[x] = false; hints[x] = HINT.eyeLine(name(y)); }
        }
      }
    } else if (layout.template === 'heads') {
      for (const [a, b] of neighbours) {
        const ha = get(a).head!, hb = get(b).head!;
        const d = Math.hypot(hb.x - ha.x, hb.y - ha.y) || 1;
        const minR = Math.min(ha.r, hb.r);
        const ov = (ha.r + hb.r - d) / (2 * minR);
        const k = pairKey(a, b);
        let l = this.heads.get(k);
        if (!l) this.heads.set(k, (l = new Latch(false, t)));
        const r = l.v ? HEADS_RANGE.exit : HEADS_RANGE.enter;
        const ok = l.step(ov >= r[0] && ov <= r[1], t, this.armHold);
        if (ok) {
          const ux = (hb.x - ha.x) / d, uy = (hb.y - ha.y) / d;
          const m = ha.r - (ha.r + hb.r - d) / 2;
          contacts.push({ a, b, kind: 'head-head' });
          contactPoints.push({ a, b, kind: 'head-head', x: ha.x + ux * m, y: ha.y + uy * m });
        } else {
          hints[a] = ov < r[0] ? HINT.leanTo(name(b)) : HINT.apart;
          hints[b] = ov < r[0] ? HINT.leanTo(name(a)) : HINT.apart;
          ready[a] = ready[b] = false;
        }
        // ghost: each head moves half of the way to a 15 % overlap
        const want = ha.r + hb.r - 0.15 * 2 * minR;
        const half = (d - want) / 2;
        const ux = (hb.x - ha.x) / d, uy = (hb.y - ha.y) / d;
        ghosts.push({ owner: a, kind: 'head', x: ha.x + ux * half, y: ha.y + uy * half, r: ha.r, matched: ok });
        ghosts.push({ owner: b, kind: 'head', x: hb.x - ux * half, y: hb.y - uy * half, r: hb.r, matched: ok });
      }
    } else if (layout.template === 'shoulder' && neighbours.length) {
      const [a0, b0] = neighbours[0];
      const engaged = (x: string, y: string) => (['left', 'right'] as Side[]).some((s) => arms[x][s].state === 'front' && arms[x][s].partner === y);
      // giver: forced by the layout, else whoever is engaged, else whose hand is closest to the target
      let raw = layout.giver && (layout.giver === a0 || layout.giver === b0) ? layout.giver : '';
      if (!raw) raw = engaged(a0, b0) ? a0 : engaged(b0, a0) ? b0 : '';
      if (!raw) {
        const dist = (x: string, y: string) => {
          const tg = handTarget(get(x), get(y));
          return Math.min(...(['left', 'right'] as Side[]).map((s) => { const h = hand(get(x), s); return Math.hypot(h.x - tg.x, h.y - tg.y) / get(x).sw!; }));
        };
        const da = dist(a0, b0), db = dist(b0, a0);
        raw = da < db * 0.8 ? a0 : db < da * 0.8 ? b0 : this.giver?.v === b0 ? b0 : a0;
      }
      if (!this.giver) this.giver = new Latch(raw, t);
      const giver = this.giver.step(raw, t, 400);
      const recv = giver === a0 ? b0 : a0;
      const A = get(giver), B = get(recv);
      const tg = handTarget(A, B);
      const ok = engaged(giver, recv);
      ghosts.push({ owner: giver, kind: 'hand', ...tg, matched: ok });
      if (!ok) {
        ready[giver] = false;
        // best arm = the one whose hand is nearest to the target
        const side = (['left', 'right'] as Side[]).map((s) => ({ s, h: hand(A, s) }))
          .sort((u, v) => Math.hypot(u.h.x - tg.x, u.h.y - tg.y) - Math.hypot(v.h.x - tg.x, v.h.y - tg.y))[0];
        const h = side.h;
        const t2 = elbowT(A, side.s, B);
        const dz = Z(A, ARM_JOINTS[side.s].wrist) - Z(A, ARM_JOINTS[side.s].shoulder);
        const reg = shoulderRegion(B, 0);
        hints[giver] = h.hidden ? HINT.showHand
          : h.y > reg.y1 ? HINT.higher
          : h.x < reg.x0 || h.x > reg.x1 ? HINT.reach(name(recv))
          : t2 == null || t2 < 0.1 || t2 > 1.3 ? HINT.elbow(name(recv))
          : dz >= 0.5 ? HINT.forward(name(recv)) : HINT.higher;
        hints[recv] = HINT.hold(name(giver));
      }
    }

    // ---- 5. depth hints + soft-z during a pending swap of overlapping neighbours
    for (let i = 0; i + 1 < base.length; i++) {
      const back = base[i], fr = base[i + 1];
      const k = back < fr ? pairKey(back, fr) : pairKey(fr, back);
      const l = this.pairs.get(k)!;
      if (l.pending) {
        depthHints[back] = depthHints[fr] = HINT.steady;
        const prog = smooth(0, this.depthHold || 1, t - l.since);
        if (!softZ) softZ = { over: fr, under: back, w: Math.round((1 - 0.5 * prog) * 1024) / 1024 };
      } else if (depth[back] - depth[fr] < 2 * DEPTH_DEADBAND && ready[back]) {
        depthHints[back] = HINT.stepUp(name(fr));
      }
    }

    const decision: Decision = { v: 1, template: layout.template, order, arms, contacts, depth, softZ };
    return { decision, interaction: { contacts, ready, hints, depthHints }, ghosts, contactPoints };
  }
}

/** Stateless single solve (no hysteresis) — for stills that come without a captured decision. */
export function solveOnce(people: SolverPerson[], layout: LayoutState, stageH = 720): SolverOutput {
  return new InteractionSolver({ armHoldMs: 0, depthHoldMs: 0 }).update(people, layout, 0, stageH);
}

/** Order of a decision restricted to the given people (missing ones dropped, unknown ones appended flat). */
export function orderFor(d: Decision | null | undefined, ids: string[]): LayerRef[] {
  const known = d ? d.order.filter((l) => ids.includes(l.id)) : [];
  const missing = ids.filter((id) => !known.some((l) => l.id === id)).sort(byId);
  return [...missing.flatMap((id) => LAYER_KINDS.map((kind) => ({ id, kind }))), ...known];
}

/** Arm states of one person from a decision (all 'none' when absent). */
export function armsFor(d: Decision | null | undefined, id: string): Record<Side, ArmDecision> {
  return d?.arms[id] ?? { left: { state: 'none', partner: null }, right: { state: 'none', partner: null } };
}

/** Same InteractionState? (used to emit only on change) */
export function sameInteraction(a: InteractionState | null, b: InteractionState): boolean {
  return !!a && JSON.stringify(a) === JSON.stringify(b);
}
